import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { DR_FUNDS, DR_SECTORS, DR_PROJECTS, DOCUMENTS } from './dataRoomData';

function countDocs(docs, { fundId, sectorId, projectId }) {
  return docs.filter(
    (d) =>
      (!fundId || d.fundId === fundId) &&
      (!sectorId || d.sectorId === sectorId) &&
      (!projectId || d.projectId === projectId)
  ).length;
}

export default function CategorySidebar({ selection, onSelect, isCollapsed, onToggleCollapse }) {
  const [expandedFunds, setExpandedFunds] = useState(() => {
    const s = new Set();
    if (selection?.fundId) s.add(selection.fundId);
    return s;
  });
  const [expandedSectors, setExpandedSectors] = useState(() => {
    const s = new Set();
    if (selection?.sectorId) s.add(selection.sectorId);
    return s;
  });

  // Auto-expand tree when selection changes from parent (e.g. clicking overview tiles)
  useEffect(() => {
    if (selection?.fundId) setExpandedFunds((prev) => new Set([...prev, selection.fundId]));
    if (selection?.sectorId) setExpandedSectors((prev) => new Set([...prev, selection.sectorId]));
  }, [selection?.fundId, selection?.sectorId]);

  const toggleFund = (fundId) => {
    setExpandedFunds((prev) => {
      const next = new Set(prev);
      next.has(fundId) ? next.delete(fundId) : next.add(fundId);
      return next;
    });
  };

  const toggleSector = (sectorId) => {
    setExpandedSectors((prev) => {
      const next = new Set(prev);
      next.has(sectorId) ? next.delete(sectorId) : next.add(sectorId);
      return next;
    });
  };

  return (
    <aside
      className={`flex-shrink-0 bg-card border-r border-border flex flex-col transition-all duration-250 ${
        isCollapsed ? 'w-14' : 'w-64'
      }`}
      style={{ minHeight: 0 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
        {!isCollapsed && (
          <span className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Browse
          </span>
        )}
        <button
          onClick={onToggleCollapse}
          className="ml-auto flex items-center justify-center w-7 h-7 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={15} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2 space-y-0">
        {/* All Documents */}
        <button
          onClick={() => onSelect({ fundId: null, sectorId: null, projectId: null })}
          title={isCollapsed ? 'All Documents' : undefined}
          className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors duration-150 ${
            !selection?.fundId
              ? 'bg-primary/10 text-primary'
              : 'text-foreground hover:bg-muted'
          }`}
        >
          <Icon
            name="FolderOpen"
            size={16}
            color={!selection?.fundId ? 'var(--color-primary)' : 'currentColor'}
            className="flex-shrink-0"
          />
          {!isCollapsed && (
            <span className="font-caption text-sm flex-1 truncate">All Documents</span>
          )}
        </button>

        {/* Fund tree */}
        {DR_FUNDS.map((fund) => {
          const fundExpanded = expandedFunds.has(fund.id);
          const fundDocCount = countDocs(DOCUMENTS, { fundId: fund.id });
          const fundSectors = DR_SECTORS.filter((s) => s.fundId === fund.id);
          const isFundActive = selection?.fundId === fund.id && !selection?.sectorId;

          return (
            <div key={fund.id}>
              {/* Fund row */}
              <button
                onClick={() => {
                  toggleFund(fund.id);
                  onSelect({ fundId: fund.id, sectorId: null, projectId: null });
                }}
                title={isCollapsed ? fund.shortLabel : undefined}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors duration-150 ${
                  isFundActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon
                  name={fundExpanded ? 'ChevronDown' : 'ChevronRight'}
                  size={12}
                  color="currentColor"
                  className="flex-shrink-0"
                />
                <Icon
                  name={fund.icon}
                  size={15}
                  color={isFundActive ? 'var(--color-primary)' : 'currentColor'}
                  className="flex-shrink-0"
                />
                {!isCollapsed && (
                  <>
                    <span className="font-caption text-sm flex-1 truncate font-medium">
                      {fund.shortLabel}
                    </span>
                    <span
                      className={`font-caption text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                        isFundActive
                          ? 'bg-primary/20 text-primary'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {fundDocCount}
                    </span>
                  </>
                )}
              </button>

              {/* Sectors */}
              {fundExpanded && !isCollapsed &&
                fundSectors.map((sector) => {
                  const sectorExpanded = expandedSectors.has(sector.id);
                  const sectorDocCount = countDocs(DOCUMENTS, { sectorId: sector.id });
                  const isSectorActive =
                    selection?.sectorId === sector.id && !selection?.projectId;
                  const sectorProjects = DR_PROJECTS.filter(
                    (p) => p.sectorId === sector.id
                  );

                  return (
                    <div key={sector.id}>
                      {/* Sector row */}
                      <button
                        onClick={() => {
                          toggleSector(sector.id);
                          onSelect({
                            fundId: fund.id,
                            sectorId: sector.id,
                            projectId: null,
                          });
                        }}
                        className={`w-full flex items-center gap-2 pl-7 pr-3 py-1.5 text-left transition-colors duration-150 ${
                          isSectorActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon
                          name={sectorExpanded ? 'ChevronDown' : 'ChevronRight'}
                          size={11}
                          color="currentColor"
                          className="flex-shrink-0"
                        />
                        <Icon
                          name={sector.icon}
                          size={14}
                          color={isSectorActive ? 'var(--color-primary)' : 'currentColor'}
                          className="flex-shrink-0"
                        />
                        <span className="font-caption text-xs flex-1 truncate">
                          {sector.label}
                        </span>
                        <span
                          className={`font-caption text-xs px-1 py-0.5 rounded flex-shrink-0 ${
                            isSectorActive
                              ? 'bg-primary/20 text-primary'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {sectorDocCount}
                        </span>
                      </button>

                      {/* Projects */}
                      {sectorExpanded &&
                        sectorProjects.map((proj) => {
                          const projDocCount = countDocs(DOCUMENTS, {
                            projectId: proj.id,
                          });
                          const isProjActive = selection?.projectId === proj.id;

                          return (
                            <button
                              key={proj.id}
                              onClick={() =>
                                onSelect({
                                  fundId: fund.id,
                                  sectorId: sector.id,
                                  projectId: proj.id,
                                })
                              }
                              className={`w-full flex items-center gap-2 pl-12 pr-3 py-1.5 text-left transition-colors duration-150 ${
                                isProjActive
                                  ? 'bg-primary/10 text-primary'
                                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                              }`}
                            >
                              <Icon
                                name={proj.icon}
                                size={13}
                                color={isProjActive ? 'var(--color-primary)' : 'currentColor'}
                                className="flex-shrink-0"
                              />
                              <span className="font-caption text-xs flex-1 truncate">
                                {proj.label}
                              </span>
                              <span
                                className={`font-caption text-xs px-1 py-0.5 rounded flex-shrink-0 ${
                                  isProjActive
                                    ? 'bg-primary/20 text-primary'
                                    : 'bg-muted text-muted-foreground'
                                }`}
                              >
                                {projDocCount}
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  );
                })}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
